// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { Description, LgSpacing, XsSpacing } from '@alto/design-system';
import React from 'react';
import { type PaymentMethodType } from '~shared/constants';
import { useCartUsers } from '~shared/features/cart/hooks/useCartUsers';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { getCheckoutPaymentAmountErrors, getCheckoutPaymentMethodErrors } from '~shared/features/checkout/helpers';
import { getShowingValidationErrors } from '~shared/features/checkout/selectors/getCheckout';
import { getOrder } from '~shared/features/checkout/selectors/getOrder';
import { findMatchingPaymentMethod } from '~shared/features/payment-methods/helpers';
import { getIsSplitPaymentEnabled } from '~shared/features/ui/selectors/getCart';
import { useOrderPricing } from '~shared/queries';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { useSelectorShared } from '~shared/store';
import { type ShipmentPaymentMethod } from '~shared/types';
import { CheckoutSplitPaymentSelector } from './CheckoutSplitPaymentSelector';
import { PaymentMethodRow } from './PaymentMethodRow';

type Props = {
  readonly handleNativePay?: (paymentMethodType: PaymentMethodType) => void;
  readonly shipmentPaymentMethods?: ShipmentPaymentMethod[];
  readonly hasCopay: boolean;
  readonly disabled?: boolean;
};

export const CheckoutPaymentSelector = ({ handleNativePay, hasCopay, shipmentPaymentMethods, disabled }: Props) => {
  const { isPaymentMethodRequired, orderPricing } = useOrderPricing();
  const showingValidationErrors = useSelectorShared(getShowingValidationErrors);
  const isSplitPaymentEnabled = useSelectorShared(getIsSplitPaymentEnabled);
  // the total cost used as the anchor point for split payments.
  // does not include tips and other fees (i.e delivery)
  const splitPaymentTotalCost = orderPricing?.subtotal || null;
  const order = useSelectorShared(getOrder);
  const { payment_method_id, second_payment_method_id } = order;
  const { paymentMethods } = useGetPaymentMethods();
  const paymentMethod = findMatchingPaymentMethod(paymentMethods, payment_method_id);
  const secondPaymentMethod = findMatchingPaymentMethod(paymentMethods, second_payment_method_id);
  const { paymentMethodValidationErrors } = getCheckoutPaymentMethodErrors({
    order,
    paymentMethods,
    isPaymentMethodRequired,
    isSplitPaymentEnabled,
  });
  const { paymentAmountValidationErrors } = getCheckoutPaymentAmountErrors({
    order,
    isSplitPaymentEnabled,
    splitPaymentsTotal: splitPaymentTotalCost,
    isPaymentMethodRequired,
  });

  const { users } = useCartUsers();
  const { deliveryMethods } = useCartDeliveryMethods();
  const hasSplitContexts = deliveryMethods.length > 1 || users.length > 1;
  const showSplitPayment = true;
  const shouldDisplaySplitPayment = !!splitPaymentTotalCost && showSplitPayment && !hasSplitContexts && hasCopay;
  const errors = showingValidationErrors ? [...paymentAmountValidationErrors, ...paymentMethodValidationErrors] : [];

  return (
    <>
      <PaymentMethodRow
        disabled={disabled}
        handleNativePay={handleNativePay}
        paymentMethod={paymentMethod}
        isSecondaryPaymentMethod={false}
        showSplitPayment={showSplitPayment}
        splitPaymentTotalCost={splitPaymentTotalCost}
        hasCopay={hasCopay}
        hasSplitContexts={hasSplitContexts}
        shipmentPaymentMethods={shipmentPaymentMethods}
        isSplitPaymentEnabled={isSplitPaymentEnabled}
      />
      {shouldDisplaySplitPayment && isSplitPaymentEnabled ? (
        <>
          <XsSpacing />
          <PaymentMethodRow
            isSecondaryPaymentMethod
            handleNativePay={handleNativePay}
            paymentMethod={secondPaymentMethod}
            showSplitPayment={showSplitPayment}
            splitPaymentTotalCost={splitPaymentTotalCost}
            hasCopay={hasCopay}
            hasSplitContexts={hasSplitContexts}
            shipmentPaymentMethods={shipmentPaymentMethods}
            isSplitPaymentEnabled={isSplitPaymentEnabled}
          />
        </>
      ) : null}
      {errors?.length ? (
        <>
          <XsSpacing />
          <Description color={COLORS.TEXT_COLORS.DANGER}>{errors.map((e) => e.message).join(' ')}</Description>
        </>
      ) : null}
      {shouldDisplaySplitPayment ? (
        <CheckoutSplitPaymentSelector
          isSplitPaymentEnabled={isSplitPaymentEnabled}
          paymentMethod={paymentMethod}
        />
      ) : (
        <LgSpacing />
      )}
    </>
  );
};
